import { router } from "@inertiajs/react"
import { Loader2, Plus } from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import { toast } from "sonner"
import { SketchDialog } from "@/components/sketch/sketch-dialog"
import { useInitialLoadReporter } from "@/components/shot-panel/use-initial-load-reporter"
import { Button, Input } from "@/components/ui/primitives"
import { EntityThumbSquare } from "@/components/workspace/entity-thumb-square"
import { useTranslation } from "@/hooks/use-translation"
import { patchShotProps, type ShotPropAssignment } from "@/lib/inertia-actions"
import { createProp } from "@/lib/props-api"
import { AssetTaskSelect, selectedAssetTaskId, useAssetTaskCandidates } from "@/components/tasks/asset-task-pick-dialog"
import { SCALE_SHEET_CANVAS_BG } from "@/lib/scale-sheet-layout"
import { cn } from "@/lib/utils"
import { UI_CARD_SELECTED, UI_CHOICE_CONTROL, UI_CHOICE_CONTROL_SM, UI_NESTED_SELECTED } from "@/lib/ui-selection"
import { fetchProjectProps, fetchShotWorkspaceDetail } from "@/lib/workspace-api"

type ProjectPropState = {
  id: number
  name: string
  reference_url?: string | null
  reference_full_url?: string | null
  reference_download_url?: string | null
}

type ProjectPropRow = {
  id: number
  name: string
  category: string | null
  reference_sheet_url: string | null
  reference_display_url: string | null
  reference_download_url?: string | null
  front_view_thumb_url?: string | null
  front_view_url?: string | null
  front_view_download_url?: string | null
  thumb_url?: string | null
  has_front_view?: boolean
  states: ProjectPropState[]
}

type ShotPropAssignmentRow = { id: number; state_id: number | null }

type Props = {
  projectId: number
  shotId: number
  onClose: () => void
  onDataChanged: () => void
  onInitialLoadSettled?: () => void
}

export function ShotPropsView({ projectId, shotId, onClose, onDataChanged, onInitialLoadSettled }: Props) {
  const { t } = useTranslation(["shot", "common"])
  const taskCandidates = useAssetTaskCandidates(projectId, "prop", true)
  const [createTaskId, setCreateTaskId] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  const [projectProps, setProjectProps] = useState<ProjectPropRow[]>([])
  const [assignments, setAssignments] = useState<Map<number, number | null>>(new Map())
  const [filter, setFilter] = useState("")
  const [newName, setNewName] = useState("")
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)
  const [preview, setPreview] = useState<{ url: string; title: string } | null>(null)
  const reportInitialLoad = useInitialLoadReporter(onInitialLoadSettled)

  const propThumb = (
    imageUrl: string | null | undefined,
    name: string,
    size: "compact" | "radio",
    frontViewChrome = false,
  ) => (
    <EntityThumbSquare
      name={name}
      imageUrl={imageUrl}
      size={size}
      imageFit={frontViewChrome ? "contain" : "cover"}
      imagePadded={frontViewChrome}
      imageBackgroundColor={frontViewChrome ? SCALE_SHEET_CANVAS_BG : undefined}
    />
  )

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setLoadError(null)

    Promise.all([fetchShotWorkspaceDetail(projectId, shotId), fetchProjectProps(projectId)])
      .then(([detail, list]) => {
        if (cancelled) return

        const { shot } = detail
        const rows: ShotPropAssignmentRow[] = ((shot as Record<string, unknown>).props as ShotPropAssignmentRow[]) ?? []

        const m = new Map<number, number | null>()
        for (const a of rows) m.set(a.id, a.state_id)
        setAssignments(m)

        setProjectProps(list)
        setFilter("")
        setNewName("")
        setCreateTaskId(null)
        setCreateError(null)
      })
      .catch((e) => {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : String(e))
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false)
          reportInitialLoad()
        }
      })

    return () => {
      cancelled = true
    }
  }, [projectId, shotId, reportInitialLoad])

  const persist = (nextAssignments: Map<number, number | null>, snapshot: Map<number, number | null>) => {
    const body: ShotPropAssignment[] = Array.from(nextAssignments.entries()).map(([prop_id, state_id]) => ({
      prop_id,
      state_id,
    }))
    patchShotProps(
      projectId,
      shotId,
      { prop_assignments: body },
      {
        onSuccess: () => toast.success(t("common.saved")),
        onError: () => {
          toast.error(t("common.saveFailed"))
          setAssignments(snapshot)
        },
        onFinish: () => onDataChanged(),
      },
    )
  }

  const toggle = (id: number) => {
    const snapshot = assignments
    const next = new Map(assignments)
    if (next.has(id)) next.delete(id)
    else next.set(id, null)
    setAssignments(next)
    persist(next, snapshot)
  }

  const changeState = (id: number, stateId: number | null) => {
    const snapshot = assignments
    const next = new Map(assignments)
    next.set(id, stateId)
    setAssignments(next)
    persist(next, snapshot)
  }

  const handleCreate = async () => {
    const name = newName.trim()
    if (!name || taskCandidates.loading || taskCandidates.error) return
    setCreating(true)
    setCreateError(null)
    try {
      const snapshot = assignments
      const resp = await createProp(projectId, {
        name,
        task_id: selectedAssetTaskId(taskCandidates.tasks, createTaskId),
      })
      const propId = resp.prop_id
      const next = new Map(assignments)
      next.set(propId, null)
      setAssignments(next)
      setNewName("")
      setCreateTaskId(null)
      const body: ShotPropAssignment[] = Array.from(next.entries()).map(([prop_id, state_id]) => ({
        prop_id,
        state_id,
      }))
      patchShotProps(
        projectId,
        shotId,
        { prop_assignments: body },
        {
          onSuccess: () => {
            onClose()
            router.visit(`/projects/${projectId}/workspace/props#prop-${propId}`)
          },
          onError: () => {
            toast.error(t("common.saveFailed"))
            setAssignments(snapshot)
            setCreating(false)
          },
          onFinish: () => onDataChanged(),
        },
      )
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : String(e))
      setCreating(false)
    }
  }

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase()
    return q
      ? projectProps.filter((r) => r.name.toLowerCase().includes(q) || (r.category ?? "").toLowerCase().includes(q))
      : projectProps
  }, [filter, projectProps])

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center p-10">
        <Loader2 className="h-6 w-6 animate-spin text-ui-text-muted" />
      </div>
    )
  }

  if (loadError) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 p-10 text-center">
        <p className="text-sm text-ui-status-failed">{loadError}</p>
        <Button type="button" size="sm" variant="ghost" onClick={onClose}>
          {t("common.close")}
        </Button>
      </div>
    )
  }

  const renderStateRadio = (opts: {
    name: string
    active: boolean
    label: string
    thumb: string | null | undefined
    full: string | null | undefined
    frontViewChrome?: boolean
    onSelect: () => void
  }) => (
    <label
      className={cn(
        "flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 transition-colors",
        opts.active
          ? UI_NESTED_SELECTED
          : "text-ui-text-secondary hover:bg-ui-bg-elevated/[0.04] hover:text-ui-text-primary",
      )}
    >
      <input
        type="radio"
        name={opts.name}
        checked={opts.active}
        onChange={opts.onSelect}
        className={UI_CHOICE_CONTROL_SM}
      />
      {opts.thumb ? (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            setPreview({
              url: (opts.full ?? opts.thumb) as string,
              title: opts.label,
            })
          }}
          className="shrink-0 cursor-zoom-in rounded-md focus-visible:ring-2 focus-visible:ring-[var(--ui-focus-ring)] focus-visible:outline-none"
          aria-label={t("props.openImage", { name: opts.label })}
        >
          {propThumb(opts.thumb, opts.label, "radio", opts.frontViewChrome)}
        </button>
      ) : (
        propThumb(null, opts.label, "radio")
      )}
      <span className="truncate text-xs font-medium">{opts.label}</span>
    </label>
  )

  return (
    <div className="flex max-h-full flex-col">
      <div className="shrink-0 px-4 pb-2">
        <h2 className="text-sm font-semibold text-ui-text-primary">{t("props.title")}</h2>
        <p className="mt-0.5 text-xs text-ui-text-muted">{t("props.hint")}</p>
      </div>
      <div className="shrink-0 border-b border-ui-border px-4 py-2.5">
        <Input
          placeholder={t("placeholders.searchNameCategory")}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
      </div>

      <div className="it2tv-scrollbar min-h-0 flex-1 overflow-y-auto p-4">
        {filtered.length === 0 ? (
          <p className="mb-3 text-sm text-ui-text-muted">{t("props.noMatching")}</p>
        ) : (
          <ul className="mb-3 space-y-2">
            {filtered.map((row) => {
              const on = assignments.has(row.id)
              const stateId = assignments.get(row.id) ?? null
              const frontViewChrome = Boolean(row.front_view_thumb_url)
              const thumbUrl = row.front_view_thumb_url ?? row.thumb_url ?? row.reference_sheet_url
              const fullUrl = row.front_view_thumb_url
                ? (row.front_view_download_url ?? row.front_view_url ?? row.front_view_thumb_url)
                : (row.reference_download_url ?? row.reference_display_url ?? row.reference_sheet_url)
              return (
                <li key={row.id}>
                  <div
                    className={cn(
                      "flex items-start gap-3 rounded-lg border px-3 py-2.5 text-sm transition-colors",
                      on
                        ? UI_CARD_SELECTED
                        : "border-ui-border bg-ui-surface-raised text-ui-text-secondary hover:border-ui-border-strong hover:bg-ui-bg-elevated/[0.05]",
                    )}
                  >
                    <label className="flex cursor-pointer items-center gap-3 pt-0.5">
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() => toggle(row.id)}
                        className={UI_CHOICE_CONTROL}
                      />
                    </label>
                    {thumbUrl ? (
                      <button
                        type="button"
                        onClick={() =>
                          setPreview({
                            url: (fullUrl ?? thumbUrl) as string,
                            title: row.name,
                          })
                        }
                        className="shrink-0 cursor-zoom-in rounded-lg focus-visible:ring-2 focus-visible:ring-[var(--ui-focus-ring)] focus-visible:outline-none"
                        aria-label={t("props.openImage", { name: row.name })}
                      >
                        {propThumb(thumbUrl, row.name, "compact", frontViewChrome)}
                      </button>
                    ) : (
                      propThumb(null, row.name, "compact")
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium text-ui-text-primary">{row.name}</div>
                      {row.category && <div className="truncate text-[11px] text-ui-text-muted">{row.category}</div>}
                      {on && row.states.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {renderStateRadio({
                            name: `prop_state_${row.id}`,
                            active: stateId == null,
                            label: t("props.baseState"),
                            thumb: thumbUrl,
                            full: fullUrl ?? thumbUrl,
                            frontViewChrome,
                            onSelect: () => changeState(row.id, null),
                          })}
                          {row.states.map((s) => (
                            <div key={s.id}>
                              {renderStateRadio({
                                name: `prop_state_${row.id}`,
                                active: stateId === s.id,
                                label: s.name,
                                thumb: s.reference_url,
                                full: s.reference_download_url ?? s.reference_full_url ?? s.reference_url,
                                onSelect: () => changeState(row.id, s.id),
                              })}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
        <div className="rounded-lg border border-dashed border-white/[0.1] bg-ui-bg-elevated/[0.02] px-3 py-2.5">
          <div className="mb-1.5 text-xs font-semibold text-ui-text-secondary">{t("props.quickAdd")}</div>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              className="min-w-[180px] flex-1"
              placeholder={t("placeholders.newPropName")}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault()
                  void handleCreate()
                }
              }}
            />
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={creating || taskCandidates.loading || Boolean(taskCandidates.error) || !newName.trim()}
              onClick={handleCreate}
            >
              {creating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />} {t("props.add")}
            </Button>
          </div>
          <AssetTaskSelect
            className="mt-2"
            tasks={taskCandidates.tasks}
            value={createTaskId}
            onChange={setCreateTaskId}
          />
          {createError || taskCandidates.error ? (
            <div className="mt-1 text-[11px] text-ui-status-failed">{createError || taskCandidates.error}</div>
          ) : null}
        </div>
      </div>

      <SketchDialog
        open={!!preview}
        onOpenChange={(o) => !o && setPreview(null)}
        projectId={projectId}
        sourceUrl={preview?.url ?? null}
        presentation="view"
        allowEdit={false}
        persist="none"
        nested
        headerTitle={preview?.title}
      />
    </div>
  )
}
